import api from './api';

export interface GlobalVariableMapping {
    id?: number;
    variable_name: string;
    act_type: string;
    value: string;
    created_at?: string;
    updated_at?: string;
}

export interface GlobalVariableMappingGroup {
    variable_name: string;
    mappings: Record<string, string>; // act_type -> value
}

export interface GlobalVariableMappingList {
    variables: GlobalVariableMappingGroup[];
}

const globalVariableService = {
    // Obter todos os mapeamentos globais
    getAllMappings: async (): Promise<GlobalVariableMapping[]> => {
        const response = await api.get('/global-variables');
        return response.data;
    },

    // Obter todos os mapeamentos agrupados por variável
    getAllMappingsGrouped: async (): Promise<GlobalVariableMappingList> => {
        const response = await api.get('/global-variables/grouped');
        return response.data;
    },

    // Obter todos os tipos de atos disponíveis
    getActTypes: async (): Promise<string[]> => {
        console.log('Chamando endpoint /global-variables/act-types');
        try {
            const response = await api.get('/global-variables/act-types');
            console.log('Resposta do endpoint /global-variables/act-types:', response.data);
            
            // Verificar se há tipos de ato duplicados
            const uniqueActTypes = Array.from(new Set(response.data)) as string[];
            if (uniqueActTypes.length !== response.data.length) {
                console.warn('Tipos de ato duplicados encontrados:', response.data);
                console.warn('Tipos de ato únicos:', uniqueActTypes);
                return uniqueActTypes;
            }
            
            // Verificar se há fluxos indisponíveis na lista
            try {
                const flowsResponse = await api.get('/flows-new');
                console.log('Verificando fluxos disponíveis:', flowsResponse.data);
                
                // Mapear fluxos disponíveis
                const availableFlowNames = flowsResponse.data
                    .filter((flow: any) => {
                        const isActive = flow.is_active === true;
                        const isAvailable = flow.available === undefined ? true : flow.available === true;
                        console.log(`Fluxo ${flow.id} - ${flow.name}: isActive=${isActive}, isAvailable=${isAvailable}, será incluído=${isActive && isAvailable}`);
                        return isActive && isAvailable;
                    })
                    .map((flow: any) => flow.name);
                
                console.log('Nomes de fluxos disponíveis:', availableFlowNames);
                
                // Filtrar apenas os tipos de ato que correspondem a fluxos disponíveis
                const filteredActTypes = uniqueActTypes.filter(actType => 
                    availableFlowNames.includes(actType)
                );
                
                console.log('Tipos de ato filtrados:', filteredActTypes);
                return filteredActTypes;
            } catch (error) {
                console.error('Erro ao verificar fluxos disponíveis:', error);
                return uniqueActTypes;
            }
        } catch (error) {
            console.error('Erro ao obter tipos de ato:', error);
            return [];
        }
    },

    // Obter mapeamentos para uma variável específica
    getMappingsByVariable: async (variableName: string): Promise<GlobalVariableMappingGroup> => {
        const response = await api.get(`/global-variables/${variableName}`);
        return response.data;
    },

    // Obter um mapeamento específico pelo ID
    getMappingById: async (mappingId: number): Promise<GlobalVariableMapping> => {
        const response = await api.get(`/global-variables/mapping/${mappingId}`);
        return response.data;
    },

    // Criar um novo mapeamento
    createMapping: async (mapping: GlobalVariableMapping): Promise<GlobalVariableMapping> => {
        const response = await api.post('/global-variables', mapping);
        return response.data;
    },

    // Atualizar um mapeamento existente
    updateMapping: async (mappingId: number, mapping: Partial<GlobalVariableMapping>): Promise<GlobalVariableMapping> => {
        const response = await api.put(`/global-variables/mapping/${mappingId}`, mapping);
        return response.data;
    },

    // Remover um mapeamento
    deleteMapping: async (mappingId: number): Promise<void> => {
        await api.delete(`/global-variables/mapping/${mappingId}`);
    },

    // Remover um mapeamento usando nome da variável e tipo de ato
    deleteMappingByVariableAndType: async (variableName: string, actType: string): Promise<void> => {
        // Primeiro, buscar o mapeamento pelo nome da variável e tipo de ato
        try {
            // Normalizar o nome da variável (remover chaves se existirem)
            const normalizedVarName = variableName.replace(/^\{|\}$/g, '');
            const varNameWithBraces = `{${normalizedVarName}}`;
            
            console.log(`Buscando mapeamento para excluir: variável normalizada=${normalizedVarName}, com chaves=${varNameWithBraces}, tipo=${actType}`);
            
            // Buscar todos os mapeamentos
            const response = await api.get('/global-variables');
            const mappings: GlobalVariableMapping[] = response.data;
            
            console.log('Todos os mapeamentos:', mappings);
            
            // Tentar encontrar o mapeamento com diferentes formatos do nome da variável
            let mapping = mappings.find(m => 
                (m.variable_name === variableName || 
                 m.variable_name === normalizedVarName || 
                 m.variable_name === varNameWithBraces) && 
                m.act_type === actType
            );
            
            console.log('Mapeamento encontrado:', mapping);
            
            if (mapping && mapping.id) {
                // Se encontrou o mapeamento, excluir pelo ID
                console.log(`Excluindo mapeamento com ID ${mapping.id}`);
                await api.delete(`/global-variables/mapping/${mapping.id}`);
            } else {
                throw new Error(`Mapeamento não encontrado para variável ${variableName} e tipo de ato ${actType}`);
            }
        } catch (error) {
            console.error('Erro ao excluir mapeamento:', error);
            throw error;
        }
    }
};

export default globalVariableService; 